import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, MessageSquare } from "lucide-react";

function Stars({ value, size = "w-4 h-4", onSelect }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onSelect} onClick={() => onSelect && onSelect(n)}
          data-testid={onSelect ? `star-${n}` : undefined}
          className={onSelect ? "cursor-pointer" : "cursor-default"}>
          <Star className={`${size} ${n <= value ? "fill-[#FFB020] text-[#FFB020]" : "text-[#525560]"}`} />
        </button>
      ))}
    </div>
  );
}

export default function CourseReviews({ course, onReload }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const reviews = course.reviews || [];

  const submit = async () => {
    if (!user) { toast.error("Inicia sesión para reseñar"); navigate("/login"); return; }
    setSaving(true);
    try {
      await api.post(`/courses/${course.id}/reviews`, { rating, comment });
      toast.success("¡Gracias por tu reseña!");
      setComment("");
      onReload && onReload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <h2 className="font-heading font-bold text-2xl">Reseñas de alumnos</h2>
        {course.review_avg != null && (
          <div className="flex items-center gap-2">
            <span className="font-mono font-bold text-xl text-[#FFB020]">{course.review_avg}</span>
            <Stars value={Math.round(course.review_avg)} />
            <span className="font-mono text-xs text-[#525560]">({course.review_count})</span>
          </div>
        )}
      </div>

      {course.enrolled && (
        <div className="bg-[#0C0D12] border border-white/10 rounded-lg p-6 mb-8">
          <p className="font-body text-sm text-[#8A8D98] mb-3">Comparte tu opinión</p>
          <div className="mb-3"><Stars value={rating} size="w-6 h-6" onSelect={setRating} /></div>
          <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="¿Qué te pareció el curso?"
            className="bg-[#13141C] border-white/10 rounded-md focus:border-[#00E5FF] focus-visible:ring-1 focus-visible:ring-[#00E5FF] text-white mb-4" rows={3} />
          <Button data-testid="submit-review-btn" onClick={submit} disabled={saving} className="bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md">
            {saving ? "Enviando..." : "Publicar reseña"}
          </Button>
        </div>
      )}

      {reviews.length === 0 ? (
        <div className="flex items-center gap-3 text-[#525560] font-body text-sm py-6">
          <MessageSquare className="w-5 h-5" /> Aún no hay reseñas. {course.enrolled ? "¡Sé el primero!" : "Compra el curso para dejar la tuya."}
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((r, i) => (
            <div key={i} data-testid={`review-${i}`} className="bg-[#0C0D12] border border-white/5 rounded-lg p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#00E5FF]/15 flex items-center justify-center font-heading font-bold text-[#00E5FF]">{r.user_name?.[0]?.toUpperCase() || "?"}</div>
                  <span className="font-heading font-semibold text-sm">{r.user_name}</span>
                </div>
                <Stars value={r.rating} />
              </div>
              {r.comment && <p className="font-body text-sm text-[#8A8D98] leading-relaxed pl-12">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
